import { createFileRoute } from '@tanstack/react-router';
import { useServerFn } from '@tanstack/react-start';
import { useEffect, useRef, useState } from 'react';

import { AuthLayout } from '~/components/auth/auth-layout.tsx';
import { useTranslations } from '~/i18n/use-translations.ts';
import { supabaseClient } from '~/integrations/supabase/base/client.ts';
import { completeOauthSignInMutation } from '~/server/auth/complete-oauth-sign-in.ts';
import { getOauthCallbackPageData } from '~/server/auth/get-oauth-callback-page-data.ts';

function validateAuthSearch(search: Record<string, unknown>) {
	return {
		redirectTo: typeof search.redirectTo === 'string' ? search.redirectTo : undefined,
	};
}

export const Route = createFileRoute('/auth/oauth/callback')({
	component: OauthCallbackPage,
	loader: () => getOauthCallbackPageData(),
	validateSearch: validateAuthSearch,
});

function OauthCallbackPage() {
	const t = useTranslations();
	const { redirectTo } = Route.useSearch();
	const completeOauthSignIn = useServerFn(completeOauthSignInMutation);
	const hasSubmitted = useRef(false);
	const [formError, setFormError] = useState<string | null>(null);

	useEffect(() => {
		const {
			data: { subscription },
		} = supabaseClient.auth.onAuthStateChange((event, supabaseSession) => {
			setTimeout(() => {
				if (event !== 'SIGNED_IN' || hasSubmitted.current) {
					return;
				}

				const refreshToken = supabaseSession?.refresh_token;
				if (!refreshToken) {
					setFormError(t('auth.signInFailed'));
					return;
				}

				hasSubmitted.current = true;
				void completeOauthSignIn({
					data: {
						redirectTo,
						refreshToken,
					},
				}).then((result) => {
					if (result?.status === 'error') {
						hasSubmitted.current = false;
						setFormError(result.formError ?? t('auth.signInFailed'));
					}
				});
			}, 0);
		});

		return () => {
			subscription.unsubscribe();
		};
	}, [completeOauthSignIn, redirectTo, t]);

	return (
		<AuthLayout>
			<div className="my-2 space-y-6 p-2">
				<div className="text-muted-foreground text-sm">{t('auth.completingSignIn')}</div>
				{formError ? <div className="text-negative text-sm italic">{formError}</div> : null}
			</div>
		</AuthLayout>
	);
}
